import React, { useState, useEffect } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { openDillToast } from '../../../../utils/helpers';
import { requestUpdateCreditApplication } from '../../../../redux/services/creditApplicationsService';

const RequestApplicationUpdateModal = ({
  open,
  handleClose,
  email,
  creditApplicationId,
}: {
  open: boolean;
  handleClose: () => void;
  email: string;
  creditApplicationId: string;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [inputFields, setInputFields] = useState<{
    email: string;
    comment: string;
  }>({ email: '', comment: '' });

  const [errorFields, setErrorFields] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!email) return;
    setInputFields((prev) => ({ ...prev, email }));
  }, [email]);

  const handleCloseModal = () => {
    setInputFields({ email: '', comment: '' });
    setErrorFields({});
    handleClose();
  };

  const handleFieldChange = (field: keyof typeof inputFields, value: string) => {
    setInputFields((prev) => ({ ...prev, [field]: value }));
    setErrorFields((prev) => ({ ...prev, [field]: '' }));
  };

  const validateFields = () => {
    const errors: { [key: string]: string } = {};
    if (!inputFields.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputFields.email)) {
      errors.email = 'Invalid email format';
    }

    setErrorFields(errors);
    return Object.keys(errors).length === 0;
  };

  const handleRequestApplicationUpdate = async () => {
    if (!validateFields()) return;

    try {
      setIsLoading(true);
      const results = await dispatch(
        requestUpdateCreditApplication({
          applicationId: creditApplicationId,
          supplierId: currentSupplier?.id || '',
          email: inputFields.email,
          ...(inputFields.comment.trim() && { comment: inputFields.comment.trim() }),
        })
      );
      if (results.type === 'creditApplications/requestUpdateCreditApplication/fulfilled') {
        openDillToast({
          type: 'SUCCESS',
          message: 'Successfully requested update',
        });
        handleCloseModal();
      } else {
        openDillToast({
          type: 'ERROR',
          message: 'Failed to request update',
        });
      }
    } catch (err) {
      openDillToast({ message: 'Failed to send link', type: 'ERROR' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Request Application Update"
      maxWidth="540px"
      contentClassName="px-4 py-3"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseModal}
            isDisabled={isLoading}
          />
          <DillButton
            label="Request"
            variant="primary"
            onClick={handleRequestApplicationUpdate}
            isLoading={isLoading}
          />
        </>
      }>
      <div className="w-full flex flex-col gap-3">
        <AppInputField
          id="email"
          label="Email"
          isRequired
          errorText={errorFields.email || ''}
          value={inputFields.email}
          onTextChange={(text) => handleFieldChange('email', text)}
        />
        <AppInputField
          id="comment"
          label="Comment"
          inputType="textarea"
          rows={4}
          placeholder="Add a comment for the applicant (optional)"
          value={inputFields.comment}
          onTextChange={(text) => handleFieldChange('comment', text)}
        />
      </div>
    </DillModal>
  );
};

export default RequestApplicationUpdateModal;
